import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X, Check, FileText, Mail, Phone, MapPin } from "lucide-react";
import { blClient, adminClient, BL_ADMIN_TOKEN_KEY } from "./client";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

async function filesToDataUrls(files) {
  return Promise.all(files.map((f) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f);
  })));
}

/**
 * Generic Listings panel — used for both For Sale and Seeking.
 * kind = "sales" | "seeking"
 */
export function BLListings({ kind, isAdmin }) {
  const isSale = kind === "sales";
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [tab, setTab] = useState("live");

  const load = async () => {
    setLoading(true);
    try {
      const r = await blClient.get(`/blackleaf/${kind}`);
      setItems(r.data.items);
      if (isAdmin) {
        const r2 = await adminClient.get(`/blackleaf/admin/${kind}/pending`);
        setPending(r2.data.items);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isAdmin]);

  const approve = async (id) => {
    await adminClient.post(`/blackleaf/admin/${kind}/${id}/approve`);
    toast.success("Approved & live"); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this listing?")) return;
    await adminClient.delete(`/blackleaf/admin/${kind}/${id}`);
    toast.success("Removed"); load();
  };
  const openInvoice = async (id) => {
    const t = localStorage.getItem(BL_ADMIN_TOKEN_KEY);
    if (!t) { toast.error("Admin login required"); return; }
    try {
      const r = await fetch(`${BACKEND}/api/blackleaf/admin/sales/${id}/invoice`, { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) {
        const msg = r.status === 401 ? "Admin session expired — sign in again" : `Couldn't load invoice (${r.status})`;
        toast.error(msg);
        return;
      }
      const html = await r.text();
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) toast.error("Pop-ups blocked — allow pop-ups for this site");
    } catch (e) {
      toast.error("Couldn't load invoice");
    }
  };

  const shown = (isAdmin && tab === "pending") ? pending : items;
  const kicker = isSale ? "For Sale" : "Seeking";
  const subtitle = isSale ? "Listings from members" : "Members searching for varieties";

  return (
    <section className="bl-section" data-testid={`bl-${kind}`}>
      <header className="bl-section-head">
        <div>
          <p className="bl-kicker">{kicker}</p>
          <h2 className="bl-h2">{subtitle}</h2>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <div className="bl-tab-pills" data-testid={`bl-${kind}-tabs`}>
              <button className={tab === "live" ? "is-on" : ""} onClick={() => setTab("live")}>Live</button>
              <button className={tab === "pending" ? "is-on" : ""} onClick={() => setTab("pending")}>Pending <span>{pending.length}</span></button>
            </div>
          )}
          <button onClick={() => setAdding(true)} className="bl-btn" data-testid={`bl-${kind}-add`}><Plus size={14}/> {isSale ? "List for sale" : "Post a search"}</button>
        </div>
      </header>

      {loading ? <Loader2 size={20} className="animate-spin mx-auto block text-bl-soft my-10"/> :
        shown.length === 0 ? <div className="bl-card p-10 text-center text-bl-soft italic">
          {tab === "pending" ? "Nothing pending — you're all caught up." : (isSale ? "No listings yet — be the first." : "No one seeking right now.")}
        </div> :
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shown.map((i) => (
            <article key={i.id} className="bl-card overflow-hidden flex flex-col" data-testid={`bl-${kind}-${i.id}`}>
              {(i.photos || [])[0] && <div className="aspect-[4/3]"><img src={i.photos[0]} alt={i.variety_name} className="w-full h-full object-cover" loading="lazy"/></div>}
              <div className="p-4 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="bl-h3">{i.variety_name}</h3>
                  {isSale && i.price != null && <span className="bl-price">{i.currency || "NZD"} ${Number(i.price).toFixed(2)}</span>}
                </div>
                {isSale && i.quantity && <p className="text-xs text-bl-soft mt-0.5">Qty: {i.quantity}</p>}
                {i.description && <p className="text-sm mt-2 line-clamp-3">{i.description}</p>}
                <div className="text-xs text-bl-soft italic mt-3 space-y-0.5">
                  <p>— {i.name}</p>
                  {i.email && <p className="not-italic flex items-center gap-1"><Mail size={11}/> <a href={`mailto:${i.email}`} className="underline">{i.email}</a></p>}
                  {i.phone && <p className="not-italic flex items-center gap-1"><Phone size={11}/> {i.phone}</p>}
                  {i.location && <p className="not-italic flex items-center gap-1"><MapPin size={11}/> {i.location}</p>}
                </div>
                {i.status === "pending" && <span className="bl-badge mt-2 self-start">Pending</span>}
                {isAdmin && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {i.status === "pending" && <button onClick={() => approve(i.id)} className="bl-btn-ghost" data-testid={`bl-${kind}-approve-${i.id}`}><Check size={12}/> Approve</button>}
                    {isSale && i.status === "approved" && <button onClick={() => openInvoice(i.id)} className="bl-btn-ghost" data-testid={`bl-invoice-${i.id}`}><FileText size={12}/> Invoice</button>}
                    <button onClick={() => remove(i.id)} className="bl-btn-ghost" data-testid={`bl-${kind}-del-${i.id}`}><Trash2 size={12}/></button>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      }

      {adding && <ListingDrawer isSale={isSale} kind={kind} onClose={() => setAdding(false)} onSaved={async () => { setAdding(false); await load(); }}/>}
    </section>
  );
}

function ListingDrawer({ isSale, kind, onClose, onSaved }) {
  const [form, setForm] = useState({
    variety_name: "", price: "", currency: "NZD", quantity: "",
    description: "", photos: [],
    name: "", email: "", phone: "", location: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pickPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const list = await filesToDataUrls(files);
    set("photos", [...form.photos, ...list]);
    e.target.value = "";
  };
  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (isSale) {
      payload.price = form.price === "" ? null : Number(form.price);
    } else {
      delete payload.price; delete payload.currency; delete payload.quantity;
    }
    setBusy(true);
    try {
      await blClient.post(`/blackleaf/${kind}`, payload);
      toast.success("Submitted — awaiting admin approval");
      onSaved();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Couldn't save"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}/>
      <aside className="absolute right-0 top-0 h-full w-full sm:max-w-lg bg-bl-paper overflow-y-auto shadow-2xl black-leaf-theme">
        <div className="sticky top-0 bg-bl-paper border-b border-bl-line flex items-center justify-between px-5 py-4">
          <h2 className="bl-h2">{isSale ? "List a variety for sale" : "Post a search"}</h2>
          <button onClick={onClose} className="bl-btn-ghost"><X size={12}/></button>
        </div>
        <form onSubmit={submit} className="px-5 py-5 space-y-3">
          <Field label="Variety *" testid="variety_name"><input className="bl-field mt-1.5" value={form.variety_name} onChange={(e) => set("variety_name", e.target.value)} required data-testid="bl-listing-variety_name"/></Field>
          {isSale && (
            <div className="grid grid-cols-3 gap-2">
              <Field label="Price"><input type="number" step="0.01" className="bl-field mt-1.5" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="bl-listing-price"/></Field>
              <Field label="Currency"><input className="bl-field mt-1.5" value={form.currency} onChange={(e) => set("currency", e.target.value)} data-testid="bl-listing-currency"/></Field>
              <Field label="Quantity"><input className="bl-field mt-1.5" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} placeholder="e.g. 5 tubers" data-testid="bl-listing-quantity"/></Field>
            </div>
          )}
          <Field label={isSale ? "Description" : "What are you after?"}><textarea className="bl-field mt-1.5" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="bl-listing-description"/></Field>
          <div className="pt-3 border-t border-bl-line">
            <p className="bl-kicker mb-2">Contact card</p>
            <Field label="Your name *"><input className="bl-field mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="bl-listing-name"/></Field>
            <Field label="Email *"><input type="email" className="bl-field mt-1.5" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="bl-listing-email"/></Field>
            <Field label="Phone"><input className="bl-field mt-1.5" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="bl-listing-phone"/></Field>
            <Field label="Location"><input className="bl-field mt-1.5" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="city / region" data-testid="bl-listing-location"/></Field>
          </div>
          <div>
            <span className="text-xs uppercase tracking-[0.18em] text-bl-soft">Photos</span>
            <div className="mt-1.5 flex flex-wrap gap-2 items-center">
              {form.photos.map((u, i) => (
                <div key={i} className="relative w-16 h-16 rounded overflow-hidden border border-bl-line">
                  <img src={u} alt="" className="w-full h-full object-cover"/>
                  <button type="button" onClick={() => set("photos", form.photos.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-bl-rose text-white text-xs w-5 h-5">×</button>
                </div>
              ))}
              <label className="bl-btn-ghost cursor-pointer">+ Photo<input type="file" accept="image/*" multiple className="hidden" onChange={pickPhoto}/></label>
            </div>
          </div>
          <button type="submit" className="bl-btn w-full mt-3" disabled={busy} data-testid="bl-listing-submit">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>}
            Submit for approval
          </button>
        </form>
      </aside>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <span className="text-xs uppercase tracking-[0.18em] text-bl-soft">{label}</span>
      {children}
    </label>
  );
}
